export class ApiClient {
    private baseUrl: string;
    public defaults: { baseURL: string };

    constructor(baseUrl: string) {
        this.baseUrl = baseUrl;
        this.defaults = { baseURL: baseUrl };
    }

    private async getAccessToken(): Promise<string | null> {
        try {
            // Method 1: Check for direct Cognito tokens (used by HostedAuthContext/EmailOnlySignup)
            const directAccessToken = localStorage.getItem('accessToken');
            if (directAccessToken) {
                return directAccessToken;
            }

            // Method 2: Try with the authority and client_id format (matching UMS client)
            let oidcUser = null;
            const authority = import.meta.env.VITE_COGNITO_AUTHORITY;
            const clientId = import.meta.env.VITE_COGNITO_CLIENT_ID;

            if (authority && clientId) {
                const storageKey = `oidc.user:${authority}:${clientId}`;
                oidcUser = sessionStorage.getItem(storageKey) || localStorage.getItem(storageKey);
            }

            // Method 3: Try to find any oidc.user key in storage
            if (!oidcUser) {
                // Check sessionStorage
                for (let i = 0; i < sessionStorage.length; i++) {
                    const key = sessionStorage.key(i);
                    if (key && key.startsWith('oidc.user:')) {
                        oidcUser = sessionStorage.getItem(key);
                        break;
                    }
                }
            }

            // Method 4: Check localStorage if not found in sessionStorage
            if (!oidcUser) {
                for (let i = 0; i < localStorage.length; i++) {
                    const key = localStorage.key(i);
                    if (key && key.startsWith('oidc.user:')) {
                        oidcUser = localStorage.getItem(key);
                        break;
                    }
                }
            }

            if (!oidcUser) {
                return null; // Return null instead of throwing error
            }

            let user;
            try {
                user = JSON.parse(oidcUser);
            } catch (error) {
                return null; // Return null instead of throwing error
            }

            if (!user.id_token) {
                return null; // Return null instead of throwing error
            }

            // Check if token is expired
            const now = Date.now() / 1000;
            if (user.expires_at && user.expires_at < now) {
                return null; // Return null instead of throwing error
            }

            return user.id_token;
        } catch (error) {
            return null; // Return null for any errors
        }
    }

    private isPublicEndpoint(endpoint: string): boolean {
        // Define public endpoints that don't require authentication
        const publicEndpoints = [
            '/api/Product',
            '/api/ProductCategory',
            '/api/BestSeller',
            '/api/ProductDetails',
            '/api/BenefitPostType',
            '/api/IngredientPostType',
            '/api/HowToUsePostType',
            '/api/Shipping',
            '/api/Tax',
            '/api/Category',
            '/api/ShopAll',
            '/api/ShopWellnessTech',
            '/api/ShopSupplements',
            '/api/TempUser',
        ];

        return publicEndpoints.some(publicEndpoint =>
            endpoint.startsWith(publicEndpoint) || endpoint.includes('/add-to-cart-url')
        );
    }

    async fetch(endpoint: string, options: RequestInit = {}): Promise<Response> {
        const token = await this.getAccessToken();
        const isPublic = this.isPublicEndpoint(endpoint);

        // Don't override Content-Type if it's already set or if we're sending FormData
        const headers: Record<string, string> = {
            ...options.headers as Record<string, string>,
        };

        // Only add Authorization header if we have a token OR if the endpoint requires auth
        if (token) {
            headers['Authorization'] = `Bearer ${token}`;
        } else if (!isPublic) {
            // If no token and endpoint requires auth, throw error
            throw new Error('Authentication required for this endpoint');
        }

        // Only set Content-Type to application/json if we're not sending FormData
        if (!(options.body instanceof FormData) && !headers['Content-Type']) {
            headers['Content-Type'] = 'application/json';
        }

        const response = await fetch(`${this.baseUrl}${endpoint}`, {
            ...options,
            headers,
        });

        return response;
    }

    async get(endpoint: string): Promise<any> {
        const response = await this.fetch(endpoint, { method: 'GET' });
        if (!response.ok) {
            let errorMessage = `HTTP error! status: ${response.status}`;
            
            try {
                const errorResponse = await response.json();
                
                // Handle AutoWrapper format
                if (errorResponse.isError && errorResponse.message) {
                    errorMessage = errorResponse.message;
                }
                // Handle direct error format
                else if (errorResponse.error) {
                    errorMessage = typeof errorResponse.error === 'string' 
                        ? errorResponse.error 
                        : JSON.stringify(errorResponse.error);
                }
            } catch (parseError) {
                // If JSON parsing fails, use the default error message
                console.warn('Could not parse error response:', parseError);
            }
            
            throw new Error(errorMessage);
        }

        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
            const jsonResponse = await response.json();
            return jsonResponse;
        }
        return null;
    }

    async post(endpoint: string, data: any): Promise<any> {
        const body = data instanceof FormData ? data : JSON.stringify(data);

        const response = await this.fetch(endpoint, {
            method: 'POST',
            body,
        });

        if (!response.ok) {
            let errorMessage = `HTTP error! status: ${response.status}`;
            
            try {
                const errorResponse = await response.json();
                
                // Handle AutoWrapper format
                if (errorResponse.isError && errorResponse.message) {
                    errorMessage = errorResponse.message;
                }
                // Handle direct error format
                else if (errorResponse.error) {
                    errorMessage = typeof errorResponse.error === 'string' 
                        ? errorResponse.error 
                        : JSON.stringify(errorResponse.error);
                }
            } catch (parseError) {
                // If JSON parsing fails, use the default error message
                console.warn('Could not parse error response:', parseError);
            }
            
            throw new Error(errorMessage);
        }

        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
            const jsonResponse = await response.json();
            return jsonResponse;
        }
        return null;
    }

    async put(endpoint: string, data: any): Promise<any> {
        const body = data instanceof FormData ? data : JSON.stringify(data);

        const response = await this.fetch(endpoint, {
            method: 'PUT',
            body,
        });

        if (!response.ok) {
            try {
                const errorResponse = await response.json();
                if (errorResponse.isError && errorResponse.message) {
                    throw new Error(errorResponse.message);
                }
            } catch (parseError) {
                // If we can't parse the error response, use status
            }
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
            const jsonResponse = await response.json();
            return jsonResponse;
        }
        return null;
    }

    async delete(endpoint: string, options: RequestInit = {}): Promise<void> {
        const response = await this.fetch(endpoint, {
            method: 'DELETE',
            ...options
        });
        if (!response.ok) {
            try {
                const errorResponse = await response.json();
                if (errorResponse.isError && errorResponse.message) {
                    throw new Error(errorResponse.message);
                }
            } catch (parseError) {
                // If we can't parse the error response, use status
            }
            throw new Error(`HTTP error! status: ${response.status}`);
        }
    }
}
